import { useState, useEffect, useCallback } from "react";
import { contactsService } from "@/api/services/contacts.service";
import { formatFullName } from "@/utils/string";
function toContactOption(contact) {
    return {
        id: contact.contactId,
        name: formatFullName(contact.firstName, contact.lastName),
        companyName: contact.companyName || null,
        email: contact.email || "",
    };
}
export function useContacts(filter, search, includeContactIds = [], type, contactType) {
    const [state, setState] = useState({
        contacts: [],
        loading: true,
        error: null,
    });
    const includeIdsKey = includeContactIds
        .filter((id) => Boolean(id))
        .map((id) => id.trim())
        .filter(Boolean)
        .sort()
        .join("|");
    const fetchContacts = useCallback(async () => {
        setState((prev) => ({ ...prev, loading: true, error: null }));
        try {
            const response = await contactsService.getContacts({
                filter: filter,
                search: search || undefined,
                type: type || undefined,
                contactType: contactType || undefined,
            });
            const baseContactOptions = response.data.data.map((contact) => toContactOption(contact));
            const byId = new Map(baseContactOptions.map((contactOption) => [
                contactOption.id,
                contactOption,
            ]));
            const includeIds = includeIdsKey
                ? includeIdsKey.split("|").filter(Boolean)
                : [];
            if (includeIds.length > 0) {
                const missingIds = includeIds.filter((id) => !byId.has(id));
                if (missingIds.length > 0) {
                    const fetchedDetails = await Promise.all(missingIds.map(async (id) => {
                        try {
                            const contactResponse = await contactsService.getContact(id);
                            return contactResponse.data;
                        }
                        catch {
                            return null;
                        }
                    }));
                    fetchedDetails
                        .filter((contact) => Boolean(contact))
                        .forEach((contact) => {
                        byId.set(contact.contactId, toContactOption(contact));
                    });
                }
            }
            setState({
                contacts: Array.from(byId.values()),
                loading: false,
                error: null,
            });
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to load contacts";
            setState((prev) => ({
                ...prev,
                loading: false,
                error: errorMessage,
            }));
        }
    }, [filter, search, includeIdsKey, type, contactType]);
    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);
    return {
        ...state,
        refetch: fetchContacts,
    };
}
